import './plugins/api/loader' // we need to load all the global functions first, before plugins can access them
import React from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import ErrorBoundary from './components/ErrorBoundary'
import { UserProvider } from './providers/UserProvider'
import { SignIn } from './modules/user/SignInOut'
import { SelectedDateProvider } from './contexts/SelectedDate'
import { Layout } from './modules/Layout'
import { CachedNotesProvider } from './contexts/CachedNotes'
import { DarkModeProvider } from './providers/DarkModeProvider'
import { HtmlHeader } from './modules/HtmlHeader'
import { SelectedRecordNameProvider } from './contexts/SelectedRecordName'
import { init as initAnalytics } from './lib/analytics'
import { NotificationProvider } from './providers/NotificationProvider'
import { GoogleMeetSummary } from './modules/GoogleMeetSummary'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import ReadOnlyEditor from './modules/editor/ReadOnlyEditor'
import { NetworkProvider } from './providers/NetworkProvider'
import { BackendManager } from './modules/BackendManager'
import { AuthenticatedUserSync } from './AuthenticatedUserSync'
import { NoteProcessorProvider } from './providers/NoteProcessorProvider'
import { GoogleCalendarDataProvider } from './contexts/GoogleCalendar/GoogleCalendarDataProvider'
import { GapiProvider } from './contexts/Gapi'
import { DevtoolsProvider } from './contexts/Devtools'
import { CalendarAuthProvider } from './contexts/CalendarAuth/CalendarAuthContext'

initAnalytics()

const queryClient = new QueryClient()

const rootElement = document.querySelector('#root')

export function MainApp() {
  return (
    <ErrorBoundary>
      <NotificationProvider>
        <NetworkProvider>
          <SignIn />
          <QueryClientProvider client={queryClient}>
            <CachedNotesProvider>
              <GapiProvider>
                <GoogleCalendarDataProvider>
                  <UserProvider>
                    <AuthenticatedUserSync />
                    <BackendManager />
                    <NoteProcessorProvider>
                      <HelmetProvider>
                        <DarkModeProvider>
                          <HtmlHeader />
                          <CalendarAuthProvider>
                            <SelectedDateProvider>
                              <SelectedRecordNameProvider>
                                <DevtoolsProvider>
                                  <Layout />
                                  <GoogleMeetSummary />
                                </DevtoolsProvider>
                              </SelectedRecordNameProvider>
                            </SelectedDateProvider>
                          </CalendarAuthProvider>
                        </DarkModeProvider>
                      </HelmetProvider>
                    </NoteProcessorProvider>
                  </UserProvider>
                </GoogleCalendarDataProvider>
              </GapiProvider>
            </CachedNotesProvider>
          </QueryClientProvider>
        </NetworkProvider>
      </NotificationProvider>
    </ErrorBoundary>
  )
}

function NotePreview() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <DarkModeProvider>
          <HtmlHeader />
          <ReadOnlyEditor />
        </DarkModeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  )
}

if (rootElement) {
  createRoot(rootElement).render(
    <React.StrictMode>
      <Router>
        <Routes>
          <Route path='/' element={<MainApp />} />
          <Route path='/join' element={<MainApp />} />
          <Route path='/join/:id' element={<MainApp />} />
          <Route path='/google-meet' element={<NotePreview />} />
        </Routes>
      </Router>
    </React.StrictMode>
  )
} else {
  document.body.innerHTML =
    '<div style="color: red; text-align: center; margin-top: 20px;">Error: Root element not found</div>'
}
